import React, { useState, useEffect } from 'react';
import {
  Card,
  FormLayout,
  TextField,
  Select,
  Button,
  Stack,
  ButtonGroup,
  Text,
  Tooltip,
  Icon,
  ChoiceList,
  Toast
} from '@shopify/polaris';
import {
  QuestionMarkMinor,
  SaveMinor,
  RefreshMinor
} from '@shopify/polaris-icons';
import { UserSettingsService } from '../../services/userSettingsService';

export default function InventoryTools({
  selectedProductIds,
  products,
  onInventoryUpdate,
  isInventoryUpdating,
  editHistory,
  user
}) {
  const [inventoryTools, setInventoryTools] = useState({
    inventory_quantity: '',
    ignoreZero: ['yes'] // Default to yes
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastError, setToastError] = useState(false);

  // Load saved inventory tools settings on component mount
  useEffect(() => {
    console.log('InventoryTools component mounted, user:', user);
    if (user?.uid) {
      loadInventorySettings();
    }
  }, [user]);

  const loadInventorySettings = async () => {
    console.log('loadInventorySettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to load settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      const savedSettings = await UserSettingsService.getUserSettings(user.uid, 'inventory');
      if (savedSettings) {
        // Ensure ignoreZero is an array
        const updatedSettings = {
          ...savedSettings,
          ignoreZero: Array.isArray(savedSettings.ignoreZero) ? 
            savedSettings.ignoreZero : 
            (savedSettings.ignoreZero ? ['yes'] : ['no'])
        };
        setInventoryTools(updatedSettings);
        showToastMessage('Inventory settings loaded successfully', false);
      }
    } catch (error) {
      console.error('Error loading inventory settings:', error);
      showToastMessage('Failed to load inventory settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const saveInventorySettings = async () => {
    console.log('saveInventorySettings called, user:', user);
    if (!user?.uid) {
      showToastMessage('You must be logged in to save settings', true);
      return;
    }
    
    setIsLoading(true);
    try {
      await UserSettingsService.saveUserSettings(user.uid, 'inventory', inventoryTools);
      showToastMessage('Inventory settings saved successfully', false);
    } catch (error) {
      console.error('Error saving inventory settings:', error);
      showToastMessage('Failed to save inventory settings', true);
    } finally {
      setIsLoading(false);
    }
  };

  const showToastMessage = (message, isError = false) => {
    setToastMessage(message);
    setToastError(isError);
    setShowToast(true);
  };

  const handleInventoryToolChange = (field, value) => {
    setInventoryTools(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleUpdateInventory = () => {
    if (selectedProductIds.size === 0) return;

    const selectedProducts = products.filter(p => selectedProductIds.has(p.id.toString()));
    const updates = [];
    const newQuantity = parseInt(inventoryTools.inventory_quantity);
    // Ensure ignoreZero is an array and has a valid value
    const ignoreZeroArray = Array.isArray(inventoryTools.ignoreZero) ? 
      inventoryTools.ignoreZero : ['yes'];
    const shouldIgnoreZero = ignoreZeroArray.includes('yes');

    if (isNaN(newQuantity)) return;

    selectedProducts.forEach(product => {
      product.variants.forEach(variant => {
        // Handle both old and new edit history structures
        let currentInventory;
        
        // Safely access the edit history state
        if (editHistory?.[product.id]?.variant_updates?.[variant.id]?.inventory_quantity !== undefined) {
          currentInventory = parseInt(editHistory[product.id].variant_updates[variant.id].inventory_quantity);
        } else {
          currentInventory = variant.inventoryQuantity || variant.inventory_quantity || 0;
        }

        // Skip if ignoreZero is true and current inventory is 0
        if (shouldIgnoreZero && currentInventory === 0) {
          console.log(`Skipping variant ${variant.id} with zero inventory`);
          return;
        }

        // Get the inventory item details - make sure it's a simple value, not a complex object
        const inventoryItemId = variant.inventory_item_id || 
                               (variant.inventoryItem ? variant.inventoryItem.id : null);

        updates.push({
          productId: product.id,
          variantId: variant.id,
          updates: {
            inventory_quantity: newQuantity,
            inventory_item_id: inventoryItemId,
            // Include additional inventory fields for UI state
            inventory_policy: variant.inventory_policy || 'deny',
            inventory_management: variant.inventory_management || 'shopify',
            old_inventory_quantity: currentInventory
          }
        });
      });
    });

    if (updates.length > 0) {
      console.log('Sending inventory updates:', updates);
      onInventoryUpdate(updates);
    }
  };

  return (
    <Card>
      {showToast && (
        <Toast
          content={toastMessage}
          error={toastError}
          onDismiss={() => setShowToast(false)}
        />
      )}
      
      <Card.Section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Text variant="headingLg">Inventory Tools</Text>
            <Tooltip content="Tools to help you manage product inventory">
              <Icon source={QuestionMarkMinor} color="base" />
            </Tooltip>
          </div>
          
          <ButtonGroup>
            <Button 
              icon={RefreshMinor} 
              onClick={loadInventorySettings} 
              disabled={isLoading || !user}
              tooltip="Load saved settings"
            >
              Load Settings
            </Button>
            <Button 
              icon={SaveMinor} 
              onClick={saveInventorySettings} 
              disabled={isLoading || !user}
              primary
              tooltip="Save current settings"
            >
              Save Settings
            </Button>
          </ButtonGroup>
        </div>
        
        <FormLayout>
          <FormLayout.Group>
            <Stack vertical spacing="tight">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Text variant="headingMd">Set Inventory Quantity</Text>
                <Tooltip content="Set inventory quantity for all selected products">
                  <Icon source={QuestionMarkMinor} color="base" />
                </Tooltip>
              </div>
              <Text variant="bodyMd" color="subdued">
                Inventory tracking needs to be activated in Shopify to let this tool work
              </Text>
              <TextField
                label="Set Inventory Quantity"
                type="number"
                value={inventoryTools.inventory_quantity}
                onChange={value => handleInventoryToolChange('inventory_quantity', value)}
                helpText="Set a specific inventory quantity for all selected products"
                autoComplete="off"
              />
              <ChoiceList
                title="Ignore products with 0 inventory"
                choices={[
                  {
                    label: 'Yes - Skip products with 0 inventory',
                    value: 'yes'
                  },
                  {
                    label: 'No - Update all products',
                    value: 'no'
                  }
                ]}
                selected={Array.isArray(inventoryTools.ignoreZero) ? inventoryTools.ignoreZero : ['yes']}
                onChange={value => handleInventoryToolChange('ignoreZero', value)}
              />
              <Button
                onClick={handleUpdateInventory}
                disabled={isInventoryUpdating}
                loading={isInventoryUpdating}
              >
                Update Inventory
              </Button>
            </Stack>
          </FormLayout.Group>
        </FormLayout>
      </Card.Section>
    </Card>
  );
}
